import {
  getErrorMessage,
  logFeatureEvent,
  reportFeatureIssue,
  type ObservabilityScope,
  getHttpStatus,
  isNetworkFailure,
} from "./shared";

type AttributionSource =
  | "revenue"
  | "revenue_config"
  | "first_last_touch"
  | "ads"
  | "meta_ads"
  | "funnel"
  | "classification"
  | "processing";

type AttributionAttributes = {
  source?: AttributionSource;
  model?: string | null;
  rowCount?: number | null;
  period?: string | null;
  httpStatus?: number | null;
};

const attributesFor = ({ source, model, rowCount, period, httpStatus }: AttributionAttributes = {}) => ({
  attribution_source: source ?? null,
  attribution_model: model ?? null,
  row_count: rowCount ?? null,
  period: period ?? null,
  http_status: httpStatus ?? null,
});

// getRequest throws a bare Error("HTTP error! status: 403") with no `.status`
// field, so getHttpStatus misses it and the message is the only signal.
const ACCESS_DENIED_MESSAGE =
  /\b(?:HTTP(?: error! status:)?\s*(?:401|403)|access denied|forbidden|unauthori[sz]ed)\b/i;

export const isAttributionAccessDenied = (error: unknown) => {
  const status = getHttpStatus(error);
  if (status === 401 || status === 403) return true;

  return ACCESS_DENIED_MESSAGE.test(getErrorMessage(error));
};

export const logAttributionEvent = (
  event: "dashboard_loaded" | "model_changed" | "no_data" | "load_degraded",
  scope: ObservabilityScope,
  attributes?: AttributionAttributes,
  options?: { dedupKey?: string },
) =>
  logFeatureEvent({
    feature: "attribution",
    event,
    scope,
    attributes: attributesFor(attributes),
    dedupKey: options?.dedupKey,
  });

// Network-failure detection is shared across observability modules; re-exported
// here under the attribution name that call sites and tests already use.
export const isAttributionNetworkFailure = isNetworkFailure;

/**
 * Attribution hooks historically swallowed failures into empty state, so a
 * broken revenue feed looked identical to a property with no sales. Failures
 * with an HTTP response still create Issues; response-less browser/network
 * failures are logged as degraded loads because they are commonly transient
 * client conditions.
 */
export const reportAttributionFailure = (
  source: AttributionSource,
  error: unknown,
  scope: ObservabilityScope,
  attributes?: AttributionAttributes,
) => {
  const httpStatus = getHttpStatus(error);

  if (isAttributionAccessDenied(error)) {
    logFeatureEvent({
      feature: "attribution",
      event: "access_denied",
      level: "warn",
      message: `Attribution: ${source} access denied`,
      scope,
      attributes: attributesFor({ source, ...attributes, httpStatus }),
      dedupKey: `access-denied:${source}:${scope.propertyId ?? "unknown"}`,
    });
    return;
  }

  if (isAttributionNetworkFailure(error)) {
    logFeatureEvent({
      feature: "attribution",
      event: "load_degraded",
      level: "warn",
      message: `Attribution: ${source} load degraded`,
      scope,
      attributes: {
        ...attributesFor({ source, ...attributes }),
        error_message: getErrorMessage(error),
      },
      dedupKey: `load-degraded:${source}:${scope.propertyId ?? "unknown"}`,
    });
    return;
  }

  reportFeatureIssue({
    feature: "attribution",
    error,
    action: `load_${source}`,
    message: `Attribution: ${source} load failed`,
    scope,
    context: attributesFor({ source, ...attributes, httpStatus }),
    dedupKey: `load-failed:${source}:${scope.propertyId ?? "unknown"}`,
  });
};
